import {Module} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import {featureFlags} from '@dms/feature-flags';
import {orchestrationRoutes} from '@dms/routes';
import {permissions} from '@dms/shared';

import {OrchestrationRoute} from './components/OrchestrationRoute/OrchestrationRoute';

/**
 * Hidden behind the ORCHESTRATION feature flag until it is enabled in
 * Flagsmith.
 *
 * **No `layoutProps`, so no entry in the main sidebar.** These pages live
 * inside Settings → General, and the nav entry that opens them is declared in
 * `SettingsNavigation` alongside the rest of that group. `register`
 * (libs/dms/shared/src/utils/register.ts) adds a menu item only for a module
 * that carries `layoutProps`, but registers its route either way — so the
 * routes below exist while the module itself stays out of the sidebar.
 *
 * `requiredPermission` matches `SettingsModule`: the pages render
 * `SettingsTemplate`, which draws the whole settings sidebar, so anyone who can
 * reach them can see the settings area. Gating on the same permission makes
 * `register` swap the element for the redirect exactly as it does for every
 * other settings page.
 *
 * **`readSettings` is a route gate, not the security boundary, and that is by
 * design rather than by omission.** An earlier version of this comment said "a
 * narrower permission arrives with the read API" — the read API arrived with
 * FE-4 and no narrower permission came with it, because the backend does not
 * express rule authority as an ACL resource at all. It decides per rule, in the
 * domain: `ReadableRuleLoader` returns only rules the actor may read,
 * `RuleActorFactory` builds the actor's authority from that rule, and the
 * aggregate's own `activationRefusal($actor)` throws on an authority failure.
 * There is consequently no orchestration resource to gate on here, and
 * inventing one would duplicate a decision the backend makes better.
 *
 * Every control is additionally bound to the server-computed
 * `availableOperations.*.allowed`, so the UI never offers a write the backend
 * would refuse. Reaching this route grants sight of the settings area, not the
 * ability to arm a rule.
 *
 * Its copy is in Phrase since T20-139103 (FE-7), so the flag no longer stands
 * between users and an untranslated namespace — this module has none.
 *
 * `/new` and `/:ruleId` draw `RulePage`. The two editor URLs left over from
 * the old pages only redirect: `/new/edit` to `/new` (it keeps a trigger seed)
 * unless it carries an assistant suggestion, and `/edit/:ruleId` to the rule.
 */
export const OrchestrationModule: Module = {
  id: moduleKeys.orchestration,
  requiredFeatureFlag: featureFlags.ORCHESTRATION_MODULE,
  requiredPermission: permissions.readSettings,
  routerProps: {
    path: orchestrationRoutes.overview,
    element: <OrchestrationRoute page="list" />,
  },
  content: [
    {
      // Registered before the detail item so React Router matches the literal
      // `/settings/automation-rules/new` ahead of the `:ruleId` pattern.
      id: `${moduleKeys.orchestration}-create`,
      routerProps: {
        path: orchestrationRoutes.create,
        element: <OrchestrationRoute page="rule" />,
      },
      requiredPermission: permissions.readSettings,
    },
    {
      // `/settings/automation-rules/new/edit` — wholly literal, so no other
      // path in this module can match it and this position carries nothing.
      // It opens an assistant suggestion and otherwise redirects to `/new`.
      id: `${moduleKeys.orchestration}-editor`,
      routerProps: {
        path: orchestrationRoutes.editor,
        element: <OrchestrationRoute page="newEditor" />,
      },
      requiredPermission: permissions.readSettings,
    },
    {
      // `/settings/automation-rules/edit/:ruleId` — the literal segment leads,
      // for the route-catalog reason spelled out on `orchestrationRoutes.edit`.
      // Unlike `create` above, this position is **not** load-bearing: the path
      // is four segments where `detail` is three, so no URL can match both and
      // there is no precedence here to preserve.
      //
      // It only redirects to the rule's own page, which the old editor links
      // and bookmarks still reach.
      id: `${moduleKeys.orchestration}-edit`,
      routerProps: {
        path: orchestrationRoutes.edit,
        element: <OrchestrationRoute page="edit" />,
      },
      requiredPermission: permissions.readSettings,
    },
    {
      id: `${moduleKeys.orchestration}-detail`,
      routerProps: {
        path: orchestrationRoutes.detail,
        element: <OrchestrationRoute page="rule" />,
      },
      requiredPermission: permissions.readSettings,
    },
  ],
};
